import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calculator, RotateCcw } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

const ScientificCalculator: React.FC = () => {
  const [display, setDisplay] = useState('0');
  const [previousValue, setPreviousValue] = useState<string | null>(null);
  const [operation, setOperation] = useState<string | null>(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);
  const [memory, setMemory] = useState(0);
  const [history, setHistory] = useState<string[]>([]);

  useSEO({
    title: 'Scientific Calculator - Advanced Online Calculator',
    description: 'Free online scientific calculator with advanced mathematical functions, trigonometry, logarithms, and scientific notation.',
    keywords: 'scientific calculator, advanced calculator, math calculator, trigonometry calculator, logarithm calculator',
    canonical: 'https://snap-tools.online/calc/scientific-calculator'
  });

  const calculate = (firstOperand: number, secondOperand: number, operation: string): number => {
    switch (operation) {
      case '+': return firstOperand + secondOperand;
      case '-': return firstOperand - secondOperand;
      case '×': return firstOperand * secondOperand;
      case '÷': return firstOperand / secondOperand;
      case '^': return Math.pow(firstOperand, secondOperand);
      case 'mod': return firstOperand % secondOperand;
      default: return secondOperand;
    }
  };

  const inputNumber = (num: string) => {
    if (waitingForOperand) {
      setDisplay(num);
      setWaitingForOperand(false);
    } else {
      setDisplay(display === '0' ? num : display + num);
    }
  };

  const inputDecimal = () => {
    if (waitingForOperand) {
      setDisplay('0.');
      setWaitingForOperand(false);
    } else if (display.indexOf('.') === -1) {
      setDisplay(display + '.');
    }
  };

  const clear = () => {
    setDisplay('0');
    setPreviousValue(null);
    setOperation(null);
    setWaitingForOperand(false);
  };

  const clearEntry = () => {
    setDisplay('0');
  };

  const performOperation = (nextOperation: string) => {
    const inputValue = parseFloat(display);

    if (previousValue === null) {
      setPreviousValue(display);
    } else if (operation) {
      const currentValue = previousValue || '0';
      const newValue = calculate(parseFloat(currentValue), inputValue, operation);
      
      const historyEntry = `${currentValue} ${operation} ${inputValue} = ${newValue}`;
      setHistory(prev => [historyEntry, ...prev.slice(0, 9)]);
      
      setDisplay(String(newValue));
      setPreviousValue(String(newValue));
    }

    setWaitingForOperand(true);
    setOperation(nextOperation);
  };

  const executeOperation = () => {
    const inputValue = parseFloat(display);

    if (previousValue !== null && operation) {
      const newValue = calculate(parseFloat(previousValue), inputValue, operation);
      
      const historyEntry = `${previousValue} ${operation} ${inputValue} = ${newValue}`;
      setHistory(prev => [historyEntry, ...prev.slice(0, 9)]);
      
      setDisplay(String(newValue));
      setPreviousValue(null);
      setOperation(null);
      setWaitingForOperand(true);
    }
  };

  const scientificFunction = (func: string) => {
    const value = parseFloat(display);
    let result: number;

    switch (func) {
      case 'sin': result = Math.sin(value * Math.PI / 180); break;
      case 'cos': result = Math.cos(value * Math.PI / 180); break;
      case 'tan': result = Math.tan(value * Math.PI / 180); break;
      case 'ln': result = Math.log(value); break;
      case 'log': result = Math.log10(value); break;
      case 'sqrt': result = Math.sqrt(value); break;
      case 'x²': result = value * value; break;
      case '1/x': result = 1 / value; break;
      case 'abs': result = Math.abs(value); break;
      case 'exp': result = Math.exp(value); break;
      case '!': result = factorial(value); break;
      case 'π': result = Math.PI; break;
      case 'e': result = Math.E; break;
      default: result = value;
    }

    const historyEntry = `${func}(${value}) = ${result}`;
    setHistory(prev => [historyEntry, ...prev.slice(0, 9)]);
    
    setDisplay(String(result));
    setWaitingForOperand(true);
  };

  const factorial = (n: number): number => {
    if (n < 0 || !Number.isInteger(n)) return NaN;
    if (n === 0 || n === 1) return 1;
    let result = 1;
    for (let i = 2; i <= n; i++) {
      result *= i;
    }
    return result;
  };

  const memoryOperation = (op: string) => {
    const value = parseFloat(display);
    switch (op) {
      case 'MC': setMemory(0); toast.success('Memory cleared'); break;
      case 'MR': setDisplay(String(memory)); setWaitingForOperand(true); break;
      case 'MS': setMemory(value); toast.success('Value stored in memory'); break;
      case 'M+': setMemory(memory + value); toast.success('Added to memory'); break;
      case 'M-': setMemory(memory - value); toast.success('Subtracted from memory'); break;
    }
  };

  const Button: React.FC<{ 
    onClick: () => void; 
    className?: string; 
    children: React.ReactNode;
    disabled?: boolean;
  }> = ({ onClick, className = '', children, disabled = false }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`p-3 rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      {children}
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-xl sm:rounded-2xl mb-4 sm:mb-6 shadow-lg">
            <Calculator className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8438ff] mb-3 sm:mb-4">
            Scientific Calculator
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Advanced calculator with scientific functions, trigonometry, logarithms, and mathematical operations 
            for complex calculations.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
          className="mb-6 sm:mb-8"
        />

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Calculator */}
          <div className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-6"
            >
              {/* Display */}
              <div className="mb-6">
                <div className="bg-gray-900 text-white p-4 rounded-lg text-right text-2xl font-mono min-h-[60px] flex items-center justify-end overflow-hidden">
                  {display}
                </div>
                {operation && previousValue && (
                  <div className="text-sm text-gray-500 mt-1 text-right">
                    {previousValue} {operation}
                  </div>
                )}
              </div>

              {/* Button Grid */}
              <div className="grid grid-cols-6 gap-2 text-sm">
                {/* Row 1 - Memory & Clear */}
                <Button onClick={() => memoryOperation('MC')} className="bg-gray-100 hover:bg-gray-200 text-gray-700">MC</Button>
                <Button onClick={() => memoryOperation('MR')} className="bg-gray-100 hover:bg-gray-200 text-gray-700">MR</Button>
                <Button onClick={() => memoryOperation('MS')} className="bg-gray-100 hover:bg-gray-200 text-gray-700">MS</Button>
                <Button onClick={() => memoryOperation('M+')} className="bg-gray-100 hover:bg-gray-200 text-gray-700">M+</Button>
                <Button onClick={() => memoryOperation('M-')} className="bg-gray-100 hover:bg-gray-200 text-gray-700">M-</Button>
                <Button onClick={clear} className="bg-red-500 hover:bg-red-600 text-white">AC</Button>

                {/* Row 2 - Scientific Functions */}
                <Button onClick={() => scientificFunction('sin')} className="bg-blue-500 hover:bg-blue-600 text-white">sin</Button>
                <Button onClick={() => scientificFunction('cos')} className="bg-blue-500 hover:bg-blue-600 text-white">cos</Button>
                <Button onClick={() => scientificFunction('tan')} className="bg-blue-500 hover:bg-blue-600 text-white">tan</Button>
                <Button onClick={() => scientificFunction('ln')} className="bg-blue-500 hover:bg-blue-600 text-white">ln</Button>
                <Button onClick={() => scientificFunction('log')} className="bg-blue-500 hover:bg-blue-600 text-white">log</Button>
                <Button onClick={clearEntry} className="bg-orange-500 hover:bg-orange-600 text-white">CE</Button>

                {/* Row 3 - More Functions */}
                <Button onClick={() => scientificFunction('sqrt')} className="bg-blue-500 hover:bg-blue-600 text-white">√</Button>
                <Button onClick={() => scientificFunction('x²')} className="bg-blue-500 hover:bg-blue-600 text-white">x²</Button>
                <Button onClick={() => scientificFunction('1/x')} className="bg-blue-500 hover:bg-blue-600 text-white">1/x</Button>
                <Button onClick={() => scientificFunction('!')} className="bg-blue-500 hover:bg-blue-600 text-white">x!</Button>
                <Button onClick={() => performOperation('^')} className="bg-purple-500 hover:bg-purple-600 text-white">x^y</Button>
                <Button onClick={() => performOperation('÷')} className="bg-purple-500 hover:bg-purple-600 text-white">÷</Button>

                {/* Row 4 - Numbers & Operations */}
                <Button onClick={() => inputNumber('7')} className="bg-gray-200 hover:bg-gray-300 text-gray-800">7</Button>
                <Button onClick={() => inputNumber('8')} className="bg-gray-200 hover:bg-gray-300 text-gray-800">8</Button>
                <Button onClick={() => inputNumber('9')} className="bg-gray-200 hover:bg-gray-300 text-gray-800">9</Button>
                <Button onClick={() => performOperation('×')} className="bg-purple-500 hover:bg-purple-600 text-white">×</Button>
                <Button onClick={() => scientificFunction('π')} className="bg-blue-500 hover:bg-blue-600 text-white">π</Button>
                <Button onClick={() => scientificFunction('e')} className="bg-blue-500 hover:bg-blue-600 text-white">e</Button>

                {/* Row 5 */}
                <Button onClick={() => inputNumber('4')} className="bg-gray-200 hover:bg-gray-300 text-gray-800">4</Button>
                <Button onClick={() => inputNumber('5')} className="bg-gray-200 hover:bg-gray-300 text-gray-800">5</Button>
                <Button onClick={() => inputNumber('6')} className="bg-gray-200 hover:bg-gray-300 text-gray-800">6</Button>
                <Button onClick={() => performOperation('-')} className="bg-purple-500 hover:bg-purple-600 text-white">-</Button>
                <Button onClick={() => scientificFunction('abs')} className="bg-blue-500 hover:bg-blue-600 text-white">|x|</Button>
                <Button onClick={() => scientificFunction('exp')} className="bg-blue-500 hover:bg-blue-600 text-white">exp</Button>

                {/* Row 6 */}
                <Button onClick={() => inputNumber('1')} className="bg-gray-200 hover:bg-gray-300 text-gray-800">1</Button>
                <Button onClick={() => inputNumber('2')} className="bg-gray-200 hover:bg-gray-300 text-gray-800">2</Button>
                <Button onClick={() => inputNumber('3')} className="bg-gray-200 hover:bg-gray-300 text-gray-800">3</Button>
                <Button onClick={() => performOperation('+')} className="bg-purple-500 hover:bg-purple-600 text-white">+</Button>
                <Button onClick={() => performOperation('mod')} className="bg-purple-500 hover:bg-purple-600 text-white">mod</Button>
                <Button onClick={() => setDisplay(prev => prev.startsWith('-') ? prev.slice(1) : '-' + prev)} className="bg-gray-400 hover:bg-gray-500 text-white">±</Button>

                {/* Row 7 */}
                <Button onClick={() => inputNumber('0')} className="bg-gray-200 hover:bg-gray-300 text-gray-800 col-span-2">0</Button>
                <Button onClick={inputDecimal} className="bg-gray-200 hover:bg-gray-300 text-gray-800">.</Button>
                <Button onClick={executeOperation} className="bg-green-500 hover:bg-green-600 text-white col-span-3">=</Button>
              </div>
            </motion.div>
          </div>

          {/* History Panel */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">History</h3>
                <button
                  onClick={() => setHistory([])}
                  className="p-1 text-gray-500 hover:text-red-500 transition-colors"
                  title="Clear history"
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {history.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">No calculations yet</p>
                ) : (
                  history.map((entry, index) => (
                    <div
                      key={index}
                      className="p-2 bg-gray-50 rounded text-xs font-mono cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => {
                        const result = entry.split(' = ')[1];
                        if (result) {
                          setDisplay(result);
                          setWaitingForOperand(true);
                        }
                      }}
                    >
                      {entry}
                    </div>
                  ))
                )}
              </div>
            </motion.div>

            {/* Memory Display */}
            {memory !== 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3"
              >
                <p className="text-sm text-blue-800">
                  <strong>Memory:</strong> {memory}
                </p>
              </motion.div>
            )}
          </div>
        </div>

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
          className="mt-8 sm:mt-12"
        />
      </div>
    </div>
  );
};

export default ScientificCalculator;
